import React, { useCallback, useEffect, useState } from 'react';
import MiniUserDetail from '../../../engage/components/MiniUserDetail/MiniUserDetail';
import styles from './comments.module.scss';
import CommentActions from './CommentActions/index';
import { useTranslation } from 'react-i18next';
import { parseString } from '../../utils/stringHelper';
import { timeStringToSeconds } from '../../utils/TimeUtil';
import { useVideoProgressContext } from '../../context/VideoProgressContext';
import { useInPlayerCommentContext } from '../../context/InPlayerCommentContext';
import cx from 'classnames';

const resolveCommentMarkRegex = /\[✅\] /g;

const Comment = ({
  comment,
  user,
  username,
  setUsername,
  handleDeleteComment,
  handleResolvedClick,
  setAddReplyTo,
  isReply,
  storyAuthor,
  onCommentReplyClick,
  onCommentEditClick,
  handleDeleteClick,
  storyId,
  setIsNewReactions,
  ignoreTimestampsBefore,
}) => {
  const [isDelete, setIsDelete] = useState(false);
  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);

  const { updateVideoSeekToValue } = useVideoProgressContext();
  const { highlightComment } = useInPlayerCommentContext();

  const { t } = useTranslation();

  const handleCommentTimestampClick = useCallback((time) => {
    const seconds = timeStringToSeconds(time);

    updateVideoSeekToValue({ value: seconds, playing: !comment.hasOwnProperty('position') });
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    if (comment.position) {
      highlightComment(comment.id);
    }
  }, [comment, updateVideoSeekToValue]);

  const renderCommentWithTimestamp = (text) => {
    const timeRegex = /\b(\d{1,2}:\d{2}(?::\d{2})?)\b/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = timeRegex.exec(text)) !== null) {
      const [time] = match;
      const startIndex = match.index;

      if (startIndex > lastIndex) {
        parts.push(text.slice(lastIndex, startIndex));
      }

      parts.push(
        <span
          key={startIndex}
          onClick={() => handleCommentTimestampClick(time)}
          className={styles.commentTimeline}
        >
          {time}
        </span>
      );

      lastIndex = startIndex + time.length;
    }

    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts;
  }

  const renderDeleteAlert = () => {
    if (isDelete) {
      return (
        <div className={styles.deleteAlert}>
          {user && comment.username && user.username !== comment.username ? (
            <div className={styles.alertMessage}>{t('general.notification.delete')}</div>
          ) : (
            <div className={styles.alertMessage}>
              {t('general.notification.delete_comment_from')} {parseString(comment.authorName)}?
            </div>
          )}
          <div className={styles.actionContainer}>
            <span className={`${styles.alertAction}`} onClick={() => setIsDelete(false)}>
              {t('general.cancel')}
            </span>
            <span
              className={`${styles.alertAction} ${styles.deleteAction}`}
              onClick={() => {
                handleDeleteComment(comment.id);
                setIsDelete(false);
              }}
            >
              {t('general.notification.comment.approve_delete_comment')}
            </span>
          </div>
        </div>
      );
    }
  };

  useEffect(() => {
    isDelete && window.scrollBy({ top: 80, left: 0, behavior: 'smooth' });
  }, [isDelete]);

  return (
    <div className={styles.commentContainer} id={`comment-${comment.id}`} style={isReply ? { marginLeft: '48px' } : { marginLeft: '0px' }}>
      <div className={styles.userDetails}>
        <MiniUserDetail
          avatar={comment?.avatar}
          name={parseString(comment.authorName)}
          classNames={{ avatarClassName: styles.userAvatar }}
        />
        {isMobileView && (
          <div className={styles.timeContainer}>
            <span className={styles.elapsedTime}>-</span>
            <span className={styles.elapsedTime}>{comment.since} ago</span>
          </div>
        )}
      </div>
      <div className={styles.commentbody}>
        <div className={cx(styles.commentContent, { [styles.resolved]: comment.resolved })} id={`comment-content-${comment.id}`}>{ignoreTimestampsBefore > comment.date ? comment.content : renderCommentWithTimestamp(comment.content.replace(resolveCommentMarkRegex, '').replace(/<br\s*[\/]?>/gi, '\n'))}</div>
        <CommentActions
          user={user}
          comment={comment}
          username={username}
          setUsername={setUsername}
          storyAuthor={storyAuthor}
          storyId={storyId}
          setAddReplyTo={setAddReplyTo}
          onCommentReplyClick={onCommentReplyClick}
          onCommentEditClick={onCommentEditClick}
          handleDeleteClick={handleDeleteClick}
          handleDeleteComment={handleDeleteComment}
          handleResolvedClick={handleResolvedClick}
          isReply={isReply}
          setIsDelete={setIsDelete}
          setIsNewReactions={setIsNewReactions}
        />
      </div>
      <div className={styles.alertContainer}>{isDelete ? renderDeleteAlert() : null}</div>
    </div>
  );
};

export default Comment;
